import PropTypes from 'prop-types';
import { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Iconify } from '../../../components';
import CallReview from '../../../pages/dashboard/callLogs/review/LazyCallReview';
import CallAudioPlayer from '../../../pages/dashboard/callLogs/CallAudioPlayer';
import { voiceCallDetails, voiceCallMedia } from './timelineCalls';

// Under a call timeline row (desk-phone `voice_call`, and Twilio calls carrying the same fields):
// the summary, an inline recording player and Read transcript (CU-869fmbgfq, CU-869fmc59c F4). The
// Timeline tab and the Overview card both use it, so a call reads the same in both: direction,
// outcome and duration are the row's title; the number, line, desk / person and call-back line are
// `showDetails` (the Timeline tab shows that line beside the title instead).
// What the viewer may not see never arrives from the backend, so nothing is hidden here.
export default function TimelineCallEntry({ act, practiceId, showDetails }) {
    const media = voiceCallMedia(act);
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState(false);
    // The recording loads only once played (the player fetches it with the user's token).
    const [playing, setPlaying] = useState(false);
    const details = showDetails ? voiceCallDetails(act) : '';
    const detailsLine = details ? (
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.4 }} data-testid="timeline-call-details">{details}</Typography>
    ) : null;
    if (!media.allowed) return detailsLine ? <Box sx={{ mt: 0.5 }}>{detailsLine}</Box> : null;
    const long = media.summary.length > 180;
    const summaryText = long && !expanded ? `${media.summary.slice(0, 180)}…` : media.summary;
    if (!media.summary && !media.summaryPending && !media.recording && !media.transcript) return detailsLine ? <Box sx={{ mt: 0.5 }}>{detailsLine}</Box> : null;
    return (
        <Box sx={{ mt: 0.75 }} data-testid={`timeline-call-${media.rowId}`}>
            {detailsLine}
            {media.summary && (
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
                    <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>Summary: </Box>
                    {summaryText}
                    {long && (
                        <Box
                            component="button"
                            type="button"
                            onClick={() => setExpanded((v) => !v)}
                            sx={{ ml: 0.5, p: 0, border: 0, bgcolor: 'transparent', color: 'primary.main', cursor: 'pointer', fontSize: 'inherit', fontWeight: 600 }}
                        >
                            {expanded ? 'Less' : 'More'}
                        </Box>
                    )}
                </Typography>
            )}
            {!media.summary && media.summaryPending && (
                <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>Summary on its way.</Typography>
            )}
            {(media.recording || media.transcript || media.rowId) && (
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    {media.recording && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setPlaying((v) => !v)}
                            aria-expanded={playing}
                            startIcon={<Iconify icon="solar:play-circle-bold" />}
                            sx={{ py: 0.25, fontSize: '0.75rem' }}
                            data-testid="timeline-call-play"
                        >
                            {playing ? 'Hide recording' : 'Play recording'}
                        </Button>
                    )}
                    {media.transcript ? (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setOpen(true)}
                            startIcon={<Iconify icon="solar:document-text-bold" />}
                            sx={{ py: 0.25, fontSize: '0.75rem' }}
                        >
                            Read transcript
                        </Button>
                    ) : (media.rowId && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setOpen(true)}
                            startIcon={<Iconify icon="solar:document-text-bold" />}
                            sx={{ py: 0.25, fontSize: '0.75rem' }}
                        >
                            Review call
                        </Button>
                    ))}
                </Stack>
            )}
            {media.recording && playing && (
                <Box sx={{ mt: 0.75 }} data-testid="timeline-call-player">
                    <CallAudioPlayer src={media.recordingUrl} />
                </Box>
            )}
            {open && media.rowId && (
                <CallReview
                    callId={String(media.rowId)}
                    list={[String(media.rowId)]}
                    practiceId={practiceId}
                    onClose={() => setOpen(false)}
                />
            )}
        </Box>
    );
}

TimelineCallEntry.propTypes = {
    act: PropTypes.object.isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    showDetails: PropTypes.bool,
};
TimelineCallEntry.defaultProps = { practiceId: undefined, showDetails: false };
